import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import { AxiosError } from "axios";
import {
  BarElement,
  Chart as ChartJS,
  Legend,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import { useAtom } from "jotai/index";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useStatDiversityPageQuery } from "@/api/hooks/stat-analytics-hooks";
import { useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import { useTableSettings } from "@/hooks/use-table-settings";
import { DataOutdatedAlert } from "@/module/service-configs-module/statistics-visualization-module/components/data-outdated-alert";
import { colorPool } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";
import { useCreateStatsTable } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/hooks/use-create-stats-table";
import { useGetBackgroundJobs } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/hooks/use-get-background-jobs";
import { useGetStatsList } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/hooks/use-get-stats-list";
import { useQueryStatsTable } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/hooks/use-query-stats-table";
import { SciChart } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/sci-chart";
import { round } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/utils";
import { isSubmitLoading } from "@/module/service-configs-module/statistics-visualization-module/store";
import {
  BackgroundJobDto,
  MoneyFlowRequestDto,
  PlayerStatsDto,
  QueryStatsTableRequestDto,
  StatFilterDtoModeEnum,
  StatItemDto,
} from "@/shared/api";
import { useApi } from "@/shared/api/use-api";
import { meStore } from "@/store/store";

ChartJS.register(LinearScale, PointElement, BarElement, Title, Tooltip, Legend);

type StatDiversityPageProps = {
  filters: MoneyFlowRequestDto | null;
  isDataOutdated: boolean;
  isActive: boolean;
};

const MINUTES_10 = 60 * 10;
const STATS_COLUMN_FILTER = ["equals", "notEquals", "between", "greaterThan", "lessThan"];
const FIRST_COLS = ["Hands", "VPIP", "PFR", "3bet"];

export const StatDiversityPage = ({
  filters,
  isDataOutdated,
  isActive,
}: StatDiversityPageProps) => {
  const { t } = useTranslation();
  const { statsControllerApi } = useApi();
  const isInitialized = useRef(false);
  const [isDurable, setIsDurable] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [isShowOtherUserJobs, setIsShowOtherUserJobs] = useState(false);
  const jobId = Object.keys(rowSelection)[0];
  const isOwner = useIsOwnerOrSuperAdmin();
  const [me] = useAtom(meStore);
  const [, setIsSubmitLoading] = useAtom(isSubmitLoading);
  const { data: statDiversityData, isLoading: isDiversityDataLoading } =
    useStatDiversityPageQuery(filters);
  const { data: statsListResponse, isLoading: isGetStatsListLoading } = useGetStatsList();
  const {
    data: jobsResponse,
    isLoading: isJobsLoading,
    refetch: refetchJobs,
  } = useGetBackgroundJobs(isShowOtherUserJobs ? undefined : me?.id, isActive);
  const checkTableTtlInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const [tableExpiringSeconds, setTableExpiringSeconds] = useState<number | null>(null);
  const [isRefreshingTable, setIsRefreshingTable] = useState(false);

  const userId = useMemo(() => {
    return jobsResponse?.data.jobs.find((item) => item.job_id === jobId)?.user_id;
  }, [jobId, jobsResponse]);

  const { mutateAsync: createStatsTable } = useCreateStatsTable();

  useEffect(() => {
    const create = async () => {
      if (!filters) {
        return;
      }

      try {
        setIsSubmitLoading(true);
        await createStatsTable({
          query: filters,
          is_durable: isDurable,
        });
        await refetchJobs();
      } catch (e) {
        const message =
          (e as unknown as AxiosError<{ message: string }>)?.response?.data?.message || e;

        enqueueSnackbar(message as string, {
          variant: "error",
          autoHideDuration: 2000,
        });
      } finally {
        setIsSubmitLoading(false);
      }
    };
    create();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsSubmitLoading, filters]);

  useEffect(() => {
    if (statDiversityData) {
      isInitialized.current = !!statDiversityData;
    }
  }, [statDiversityData]);

  useEffect(() => {
    if (jobId) {
      checkTableTtlInterval.current = setInterval(() => {
        const currentJob = jobsResponse?.data.jobs.find((item) => item.job_id === jobId);
        if (currentJob?.result_expires_at) {
          setTableExpiringSeconds(
            Math.abs(dayjs(currentJob.result_expires_at).diff(new Date(), "seconds"))
          );
        }
      }, 1000);
    }

    return () => {
      if (checkTableTtlInterval.current) {
        clearInterval(checkTableTtlInterval.current);
      }
    };
  }, [jobId, jobsResponse?.data.jobs]);

  const firstStats = useMemo(() => {
    const filtered =
      statsListResponse?.data.items.filter((item) => FIRST_COLS.includes(item.stat_name)) ||
      [];

    return FIRST_COLS.map((item) => {
      return filtered.find((stat) => stat.stat_name === item) as StatItemDto;
    }).filter((item) => !!item);
  }, [statsListResponse?.data.items]);

  const columns = useMemo<MRT_ColumnDef<PlayerStatsDto>[]>(() => {
    return [
      {
        header: t("pid"),
        accessorKey: "player_id",
        filterFn: "equals",
        enableColumnFilterModes: false,
      },
      {
        header: t("aiProfile"),
        accessorKey: "ai_profile",
        filterFn: "equals",
        enableColumnFilterModes: false,
      },
      {
        header: t("totalBBProfit"),
        accessorKey: "total_bb_profit",
        filterFn: "equals",
        enableColumnFilterModes: false,
        Cell: ({ cell }) => {
          if (cell.row.original.ai_profile === "") {
            const value = cell.getValue<number>();
            if (value) {
              return round(cell.getValue<number>());
            }

            return value || "0";
          }
          return "";
        },
      },
      ...(firstStats?.map((item) => {
        let header = item.stat_name;
        if (item.stat_name === "Hands") {
          header = t("hands");
        }
        return {
          id: item.stat_id,
          header,
          accessorFn: ({ stats }) => {
            return stats?.[item.stat_id || item.stat_name];
          },
          columnFilterModeOptions: STATS_COLUMN_FILTER,
          Cell: ({ cell }) => {
            const value = cell.getValue<number>();
            if (value) {
              return round(cell.getValue<number>());
            }

            return value || "0";
          },
        } as MRT_ColumnDef<PlayerStatsDto>;
      }) || []),
      ...(statsListResponse?.data.items
        .filter((item) => !FIRST_COLS.includes(item.stat_name))
        .sort((a, b) => a.stat_name.localeCompare(b.stat_name))
        .map((item) => {
          return {
            id: item.stat_id,
            header: item.stat_name,
            accessorFn: ({ stats }) => {
              return stats?.[item.stat_id];
            },
            Cell: ({ cell }) => {
              const value = cell.getValue<number>();
              if (value) {
                return round(cell.getValue<number>());
              }

              return value || "0";
            },
            muiTableHeadCellProps: {
              sx: {
                minWidth: "175px",
              },
            },
            columnFilterModeOptions: STATS_COLUMN_FILTER,
          } as MRT_ColumnDef<PlayerStatsDto>;
        }) || []),
    ];
  }, [t, statsListResponse, firstStats]);

  const { defaultProps, state } = useTableSettings<PlayerStatsDto>("statsDiversity");

  const columnFilterFns = useMemo(() => {
    return {
      ...(statsListResponse?.data.items.reduce<Record<string, string>>((acc, item) => {
        acc[item.stat_id] = "between";
        return acc;
      }, {}) || {}),
      ...state.columnFilterFns,
    };
  }, [statsListResponse?.data.items, state.columnFilterFns]);

  const statsFilters: QueryStatsTableRequestDto = useMemo(() => {
    const filterFns = Object.keys(columnFilterFns);
    const playerIdFilter = state.columnFilters.find((item) => item.id === "player_id");
    const aiProfileFilter = state.columnFilters.find((item) => item.id === "ai_profile");

    return {
      pid_filter: {
        pids: playerIdFilter ? [playerIdFilter.value as string] : [],
      },
      ai_profile_filter: {
        ai_profiles: aiProfileFilter ? [aiProfileFilter.value as string] : [],
      },
      stat_filters: state.columnFilters
        .filter((item) => item.id !== "player_id" && item.id !== "ai_profile")
        .map((item) => {
          return {
            stat_id: item.id,
            mode: columnFilterFns[
              item.id as keyof typeof filterFns
            ].toUpperCase() as StatFilterDtoModeEnum,
            stat_value: Array.isArray(item.value)
              ? item.value.join(",")
              : (item.value as string),
          };
        }),
    };
  }, [state.columnFilters, columnFilterFns]);

  const { data: statsResponse, isLoading: isStatsTableLoading } = useQueryStatsTable({
    jobId,
    userId,
    pageNumber: state.pagination.pageIndex,
    pageSize: state.pagination.pageSize,
    filters: statsFilters,
  });

  const table = useMaterialReactTable({
    data: statsResponse?.data.items || [],
    columns: columns,
    getRowId: (originalRow) => originalRow.player_id,
    ...defaultProps,
    initialState: {
      ...defaultProps.initialState,
      sorting: [{ id: "name", desc: false }],
    },
    enableGrouping: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    groupedColumnMode: "reorder",
    manualPagination: true,
    manualFiltering: true,
    rowCount: statsResponse?.data.pagination.items_total,
    state: {
      ...state,
      columnFilterFns,
      sorting: state.sorting.length > 0 ? state.sorting : [{ id: "player_id", desc: false }],
      isLoading: isStatsTableLoading,
      pagination: {
        pageSize: statsResponse?.data.pagination.page_size || state.pagination.pageSize,
        pageIndex: statsResponse?.data.pagination.page_number || state.pagination.pageIndex,
      },
    },
    muiToolbarAlertBannerProps: {
      sx: {
        "& .MuiAlert-message": {
          minWidth: "500px !important",
        },
      },
    },
    renderTopToolbarCustomActions: () => {
      return (
        <Button variant="contained" onClick={handleExportStatsTable} loading={isExportLoading}>
          {t("export")}
        </Button>
      );
    },
    muiTableBodyRowProps: ({ row }) => {
      if (row.original.ai_profile === "") {
        if (row.original.total_bb_profit >= 0) {
          return {
            sx: {
              backgroundColor: alpha(colorPool[1], 0.25),
            },
          };
        } else {
          return {
            sx: {
              backgroundColor: alpha(colorPool[0], 0.25),
            },
          };
        }
      }

      return {};
    },
  });

  const handleExportStatsTable = async () => {
    if (isExportLoading || !jobId || !userId) {
      return;
    }

    try {
      setIsExportLoading(true);
      const response = await statsControllerApi.exportStatsTableCsv(userId, jobId, {
        responseType: "blob",
      });
      const contentDisposition = response?.headers["content-disposition"];
      if (contentDisposition) {
        const fileName = contentDisposition.split("filename=")[1].split(";")[0];
        const url = URL.createObjectURL(response.data as Blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (e) {
      const message =
        (e as unknown as AxiosError<{ message: string }>)?.response?.data?.message || e;

      enqueueSnackbar(message as string, {
        variant: "error",
        autoHideDuration: 2000,
      });
    } finally {
      setIsExportLoading(false);
    }
  };

  const { defaultProps: defaultJobsTableProps, state: defaultJobsState } =
    useTableSettings<BackgroundJobDto>("statsTableJobs", {
      pageSize: 10,
    });

  const jobColumns = useMemo<MRT_ColumnDef<BackgroundJobDto>[]>(() => {
    return [
      {
        header: t("jobId"),
        accessorKey: "job_id",
        id: "job_id",
      },
      {
        header: t("status"),
        accessorKey: "status",
        id: "status",
        Cell: ({ row: { original } }) => {
          switch (original.status) {
            case "FAILED":
              return t("dataProcessingError");
            case "EXPIRED":
              return t("evicted");
            case "DONE":
              return t("ready");
            case "PENDING":
            case "RUNNING":
            case "FINALIZING":
              return (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <span>{t("created")}</span>
                  <CircularProgress size={16} />
                </Box>
              );
            default:
              return original.status;
          }
        },
      },
      {
        header: t("userId"),
        accessorKey: "user_id",
        id: "user_id",
      },
      {
        header: t("createdAt"),
        accessorKey: "created_at",
        id: "created_at",
        Cell: ({ row: { original } }) =>
          original.created_at ? dayjs(original.created_at).format("DD.MM.YYYY HH:mm") : "-",
      },
      {
        header: t("evictionAt"),
        accessorKey: "result_expires_at",
        id: "result_expires_at",
        Cell: ({ row: { original } }) =>
          original.result_expires_at
            ? dayjs(original.result_expires_at).format("DD.MM.YYYY HH:mm")
            : "-",
      },
    ];
  }, [t]);

  const jobsTable = useMaterialReactTable({
    data: jobsResponse?.data?.jobs || [],
    columns: jobColumns,
    getRowId: (originalRow) => originalRow.job_id,
    ...defaultJobsTableProps,
    initialState: {
      ...defaultJobsTableProps.initialState,
    },
    enableColumnOrdering: true,
    enableColumnDragging: true,
    enableColumnFilterModes: true,
    enableRowSelection: ({ original }) => {
      return original.status === "DONE";
    },
    enableMultiRowSelection: false,
    onRowSelectionChange: (param) => {
      setRowSelection(param);
    },
    state: {
      ...defaultJobsState,
      rowSelection,
      isLoading: isJobsLoading,
    },
    muiToolbarAlertBannerProps: {
      sx: {
        "& .MuiAlert-message": {
          minWidth: "500px !important",
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        width: "100%",
      },
    },
    renderTopToolbarCustomActions: () => {
      return isOwner ? (
        <FormControlLabel
          control={
            <Checkbox
              checked={isShowOtherUserJobs}
              onChange={(e) => setIsShowOtherUserJobs(e.target.checked)}
            />
          }
          label={t("showOtherUserSessions")}
        />
      ) : null;
    },
  });

  const buttonsContainer = document.querySelector(
    "#statisticVisualizationFiltersButtonsContainer"
  );

  return (
    <>
      <Grid
        container
        rowSpacing={2}
        columnSpacing={2}
        sx={{
          paddingY: 2,
        }}
      >
        {isGetStatsListLoading ? (
          <CircularProgress sx={{ margin: "96px auto 32px" }} />
        ) : (
          <>
            <Stack sx={{ display: "flex", gap: 2, alignItems: "flex-start", width: "100%" }}>
              {userId &&
                tableExpiringSeconds !== null &&
                tableExpiringSeconds < MINUTES_10 && (
                  <Alert
                    severity="warning"
                    sx={{ width: "100%", display: "flex", alignItems: "center" }}
                  >
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      {tableExpiringSeconds < 60
                        ? t("sessionExpiringSeconds", {
                            seconds: Math.round(tableExpiringSeconds),
                          })
                        : t("sessionExpiringMinutes", {
                            minutes: Math.round(tableExpiringSeconds / 60),
                          })}
                      <Button
                        variant="contained"
                        size="small"
                        loading={isRefreshingTable}
                        onClick={async () => {
                          if (isRefreshingTable) {
                            return;
                          }
                          try {
                            setIsRefreshingTable(true);
                            await statsControllerApi.refreshStatsTable(userId, jobId);
                            await refetchJobs();
                          } catch (e) {
                            const message =
                              (e as unknown as AxiosError<{ message: string }>)?.response?.data
                                ?.message || e;

                            enqueueSnackbar(message as string, {
                              variant: "error",
                              autoHideDuration: 2000,
                            });
                          } finally {
                            setIsRefreshingTable(false);
                          }
                        }}
                      >
                        {t("extendSession")}
                      </Button>
                    </Box>
                  </Alert>
                )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="subtitle2">
                  {t("jobId")} {jobId || "-"}
                </Typography>
              </Box>
              <MaterialReactTable table={jobsTable} />
            </Stack>

            {jobId && (
              <>
                <DataOutdatedAlert isDataOutdated={isDataOutdated} />
                <Box sx={{ display: "flex", minHeight: "400px", width: "100%" }}>
                  {isDiversityDataLoading && !isInitialized.current ? (
                    <CircularProgress sx={{ margin: "96px auto 32px" }} />
                  ) : (
                    <Box
                      sx={{
                        height: "700px",
                        width: "100%",
                        mb: 2,
                      }}
                    >
                      <MaterialReactTable table={table} />
                      <Box sx={{ mt: 4 }} />
                      {userId && jobId && (
                        <SciChart
                          userId={userId}
                          jobId={jobId}
                          stats={statsListResponse?.data.items || []}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Grid>
      {!!buttonsContainer &&
        createPortal(
          <Box sx={{ position: "relative", bottom: "6px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDurable}
                  onChange={() => setIsDurable((prevState) => !prevState)}
                />
              }
              label={t("durable")}
            />
          </Box>,
          buttonsContainer
        )}
    </>
  );
};
